import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { 
  getSystemAssets,
  getViewSystemData
} from '../../../models/assetsModel';
import ViewSystemModal from './modals/ViewSystemModal';
import EditSystemModal from './modals/EditSystemModal';
import AddSystemModal from './modals/AddSystemModal';
import '../../../styles/colors.css';

// Helper function to calculate days remaining
const calculateDaysRemaining = (expiryDate) => {
  if (!expiryDate) return 0;
  const today = new Date();
  const expiry = new Date(expiryDate);
  const diffTime = expiry - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
};

function SystemAssets() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);

  // Handle view asset
  const handleView = (asset) => {
    setSelectedAsset(asset);
    setShowViewModal(true);
  };

  // Handle edit asset
  const handleEdit = (asset) => {
    setSelectedAsset(asset);
    setShowEditModal(true);
  };

  // Handle add asset
  const handleAdd = () => {
    setShowAddModal(true);
  };

  const initialPagination = () => {
    const searchParams = new URLSearchParams(location.search);
    const page = parseInt(searchParams.get('page')) || 1;
    const pageSize = parseInt(searchParams.get('pageSize')) || 20;
    return {
      pageIndex: page - 1,
      pageSize
    };
  };

  const [pagination, setPagination] = useState(initialPagination);
  const [globalFilter, setGlobalFilter] = useState(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('search') || '';
  });
  const [sorting, setSorting] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      params.append('page', pagination.pageIndex + 1);
      params.append('pageSize', pagination.pageSize);
      
      if (globalFilter) {
        params.append('search', globalFilter);
      }
      
      if (sorting.length > 0) {
        params.append('sortBy', sorting[0].id);
        params.append('sortOrder', sorting[0].desc ? 'DESC' : 'ASC');
      }
       
      const response = await getSystemAssets(params);
      
      if (response.status === 1 && response.data) {
        // Handle nested data structure: response.data.data.rows
        const nestedData = response.data.data || response.data;
        const dataRows = nestedData.rows || nestedData;
        setAssets(Array.isArray(dataRows) ? dataRows : []);
        setRowCount(nestedData.total || (Array.isArray(dataRows) ? dataRows.length : 0));
      } else {
        toast.error(response.message || 'Failed to load system assets');
        setAssets([]);
        setRowCount(0);
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred while loading system assets');
      setAssets([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [pagination, globalFilter, sorting]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  useEffect(() => {
    // Update the URL when pagination or filters change
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', pagination.pageIndex + 1);
    searchParams.set('pageSize', pagination.pageSize);
    
    if (globalFilter) {
      searchParams.set('search', globalFilter);
    } else {
      searchParams.delete('search');
    }
    
    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    }, { replace: true });
  }, [pagination, globalFilter, navigate, location.pathname, location.search]);

  // Handle pagination change
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, pageIndex: newPage }));
  };

  // Handle entries per page change
  const handleEntriesPerPageChange = (e) => {
    const pageSize = Number(e.target.value);
    setPagination(prev => ({ ...prev, pageIndex: 0, pageSize }));
  };

  // Build pagination controls
  const totalPages = Math.ceil(rowCount / pagination.pageSize);
  const currentPage = pagination.pageIndex + 1;

  // Check permissions after all hooks are defined
  if (!can('View Assets')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-lg font-semibold mb-2">Access Denied</div>
          <div className="text-gray-600">You do not have permission to view system assets.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section with Logo and Title */}
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 rounded-xl shadow-lg" style={{ background: 'var(--gradient-primary)' }}>
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                  System Assets
                </h1>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Manage system infrastructure and platforms
                </p>
                <div className="flex items-center mt-2">
                  <span className="text-sm font-medium text-brand-primary">
                    {rowCount ? `Showing ${Math.min(pagination.pageSize, rowCount)} of ${rowCount} results` : '0 results'}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
         
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4" style={{ color: 'var(--text-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search system assets..."
                    value={globalFilter ?? ""}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border rounded-lg text-sm transition-all duration-200"
                    style={{
                      borderColor: 'var(--border-light)',
                      backgroundColor: 'var(--bg-card)',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>
              </div>
              {can('Manage Assets') && (
                <button
                  onClick={handleAdd}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-primary hover:opacity-90 transition-all duration-200"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add System
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Assets Grid */}
        {loading ? (
          <div className="bg-white rounded-xl p-12 shadow-lg border border-gray-100 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderBottomColor: 'var(--color-primary)' }}></div>
            <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>Loading system assets...</p>
          </div>
        ) : assets.length === 0 ? (
          <div className="bg-white rounded-xl p-12 shadow-lg border border-gray-100 text-center">
            <div className="max-w-sm mx-auto">
              <div className="rounded-full p-6 w-20 h-20 mx-auto mb-4" style={{ backgroundColor: 'var(--bg-primary)' }}>
                <svg className="w-8 h-8 mx-auto" style={{ color: 'var(--text-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>No system assets found</h3>
              <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
                {globalFilter ? "No assets match your search criteria." : "Get started by adding your first system asset."}
              </p>
              {can('Manage Assets') && (
                <button
                  onClick={handleAdd}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-primary hover:opacity-90 transition-all duration-200"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add System
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {assets.map((asset) => (
              <div key={asset.asset_id} className="bg-white rounded-xl p-6 shadow-lg border hover:shadow-xl transition-all duration-200 group" style={{ borderColor: 'var(--border-light)' }}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg shadow-sm" style={{ backgroundColor: 'var(--color-primary)' }}>
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold group-hover:text-brand-primary transition-colors" style={{ color: 'var(--text-primary)' }}>
                        {asset.asset_name}
                      </h3>
                      {asset.vendor && (
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{asset.vendor}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {can('View Assets') && (
                      <button
                        onClick={() => handleView(asset)}
                        className="inline-flex items-center p-2 border rounded-lg text-sm font-medium bg-white hover:shadow-md transition-all duration-200"
                        style={{
                          borderColor: 'var(--color-accent)',
                          color: 'var(--color-accent)'
                        }}
                        title="View Details"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    )}
                    {can('Manage Assets') && (
                      <button
                        onClick={() => handleEdit(asset)}
                        className="inline-flex items-center p-2 border rounded-lg text-sm font-medium bg-white hover:shadow-md transition-all duration-200"
                        style={{
                          borderColor: 'var(--color-primary)',
                          color: 'var(--color-primary)'
                        }}
                        title="Edit Asset"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* Status Badge */}
                <div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    asset.asset_status === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    <div className={`w-1.5 h-1.5 rounded-full mr-1 ${
                      asset.asset_status === 1 ? 'bg-green-400' : 'bg-red-400'
                    }`}></div>
                    {asset.asset_status === 1 ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bottom Pagination Controls */}
        {assets.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 mt-6">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Items per page:</span>
                <select
                  value={pagination.pageSize}
                  onChange={handleEntriesPerPageChange}
                  className="block w-20 px-3 py-2 border rounded-lg text-sm transition-all duration-200"
                  style={{
                    borderColor: 'var(--border-light)',
                    backgroundColor: 'var(--bg-card)',
                    color: 'var(--text-primary)'
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={40}>40</option>
                  <option value={60}>60</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>entries per page</span>
              </div>

              <span className="flex items-center gap-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                Showing {Math.min(rowCount, pagination.pageIndex * pagination.pageSize + 1)} to {Math.min(rowCount, (pagination.pageIndex + 1) * pagination.pageSize)} of {rowCount} entries
              </span>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(0)}
                  disabled={currentPage === 1}
                  className="inline-flex items-center px-3 py-2 border rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    borderColor: 'var(--border-medium)',
                    color: 'var(--text-primary)',
                    backgroundColor: 'var(--bg-card)'
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => handlePageChange(pagination.pageIndex - 1)}
                  disabled={currentPage === 1}
                  className="inline-flex items-center px-3 py-2 border rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    borderColor: 'var(--border-medium)',
                    color: 'var(--text-primary)',
                    backgroundColor: 'var(--bg-card)'
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <span className="flex items-center gap-1 text-sm px-4" style={{ color: 'var(--text-secondary)' }}>
                  Page{' '}
                  <strong style={{ color: 'var(--text-primary)' }}>
                    {pagination.pageIndex + 1} of{' '}{totalPages}
                  </strong>
                </span>
                
                <button
                  onClick={() => handlePageChange(pagination.pageIndex + 1)}
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center px-3 py-2 border rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    borderColor: 'var(--border-medium)',
                    color: 'var(--text-primary)',
                    backgroundColor: 'var(--bg-card)'
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <button
                  onClick={() => handlePageChange(totalPages - 1)}
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center px-3 py-2 border rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    borderColor: 'var(--border-medium)',
                    color: 'var(--text-primary)',
                    backgroundColor: 'var(--bg-card)'
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showViewModal && selectedAsset && (
        <ViewSystemModal 
          isOpen={showViewModal}
          system={selectedAsset} 
          onClose={() => setShowViewModal(false)} 
        />
      )}
      
      {showEditModal && selectedAsset && (
        <EditSystemModal 
          isOpen={showEditModal}
          system={selectedAsset} 
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchAssets(); // Refresh the list
          }}
        />
      )}

      {showAddModal && (
        <AddSystemModal 
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchAssets(); // Refresh the list
            toast.success('System created successfully');
          }}
        />
      )}
    </div> 
  );
}

export default SystemAssets;
